import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import MLIcon from '../MLIcon';
import s from './MLAlert.module.css';

function getAlertProps(alertType) {
  let alertClass;
  let alertIconType;
  switch (alertType) {
    case 'error':
      alertClass = s.error;
      alertIconType = 'not';
      break;
    case 'warning':
      alertClass = s.warning;
      alertIconType = 'alert_outline';
      break;
    case 'success':
      alertClass = s.success;
      alertIconType = 'circle_check_outline';
      break;
    case 'notification':
      alertClass = s.notification;
      alertIconType = 'help_outline';
      break;
    case 'sync':
      alertClass = s.sync;
      alertIconType = 'refresh';
      break;
    default:
      alertClass = s.success;
      alertIconType = 'circle_check_outline';
  }
  return { alertClass, alertIconType };
}

const navSelector = '[data-id="nav-container"]';
const forceUpdateEvents = [
  'webkitTransitionEnd',
  'otransitionend',
  'oTransitionEnd',
  'msTransitionEnd',
  'transitionend',
];

class MLAlert extends Component {
  static propTypes = {
    className: PropTypes.string,
    alertType: PropTypes.string,
    dismissTime: PropTypes.number, // milliseconds default 3000
    dismissible: PropTypes.bool,
    onDismiss: PropTypes.func, // triggered when dismissed.
    onRequestClose: PropTypes.func, // triggered when user requests to dismiss.
    // eslint-disable-next-line react/no-unused-prop-types
    alertPosition: PropTypes.object,
    text: PropTypes.string,
    linkEl: PropTypes.object, // aTag Element spected
    show: PropTypes.bool.isRequired,
    top: PropTypes.number, // distance from the top of the screen;
    setTimeout: PropTypes.func,
    clearTimeout: PropTypes.func,
    ignoreNavContainer: PropTypes.bool, // if true, do not position self based on nav-container
    focusOnShow: PropTypes.bool,
    focusDelay: PropTypes.number,
  };

  static defaultProps = {
    focusOnShow: false,
    focusDelay: 0,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.show !== nextProps.show) {
      return {
        showClass: nextProps.show ? s.shown : '',
        show: nextProps.show,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      show: this.props.show || false,
      showClass: this.props.show ? s.shown : '',
    };
    this.alertRef = createRef();
    this.previousFocus = null;
    this.focusTimeoutId = null;
  }

  componentDidMount() {
    const { dismissible, dismissTime } = this.props;
    if (!dismissible || (dismissible && dismissTime)) {
      this.props.setTimeout(this.dismiss, dismissTime || 3000);
    }
    this.addForceUpdateEvents();
    if (this.props.show) {
      this.scheduleFocus();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      this.scheduleFocus();
    }
  }

  componentWillUnmount() {
    this.clearFocusTimeout();
    this.removeForceUpdateEvents();
  }

  clearFocusTimeout = () => {
    if (this.focusTimeoutId != null) {
      this.props.clearTimeout(this.focusTimeoutId);
      this.focusTimeoutId = null;
    }
  };

  scheduleFocus = () => {
    const { focusOnShow, focusDelay, setTimeout } = this.props;

    if (!focusOnShow) {
      return;
    }

    this.clearFocusTimeout();
    this.previousFocus = document.activeElement;

    this.focusTimeoutId = setTimeout(() => {
      this.focusTimeoutId = null;
      this.alertRef.current?.focus();
    }, focusDelay);
  };

  restoreFocus = () => {
    if (
      this.previousFocus &&
      typeof this.previousFocus.focus === 'function' &&
      document.contains(this.previousFocus)
    ) {
      this.previousFocus.focus();
    }
    this.previousFocus = null;
  };

  getNavContainer() {
    if (this.navContainer) {
      return this.navContainer;
    }
    this.navContainer = document.querySelector(navSelector);
    return this.navContainer;
  }

  addForceUpdateEvents() {
    const navContainer = this.getNavContainer();
    if (navContainer) {
      forceUpdateEvents.forEach((event) => {
        navContainer.addEventListener(event, this.myForceUpdate);
      });
    }
  }

  removeForceUpdateEvents() {
    const navContainer = this.getNavContainer();
    if (navContainer) {
      forceUpdateEvents.forEach((event) => {
        navContainer.removeEventListener(event, this.myForceUpdate);
      });
    }
  }

  myForceUpdate = () => {
    this.forceUpdate();
  };

  handleDismiss = (event) => {
    this.clearFocusTimeout();
    this.setState({ showClass: '' });
    this.restoreFocus();
    if (this.props.onRequestClose) {
      this.props.onRequestClose(event);
    }
    if (this.props.onDismiss) {
      this.props.onDismiss();
    }
  };

  dismiss = () => {
    this.handleDismiss();
  };

  getAlertA11yProps() {
    const { focusOnShow } = this.props;

    return {
      ref: this.alertRef,
      ...(focusOnShow && { tabIndex: -1 }),
    };
  }

  renderDismissEl() {
    return (
      <div className={s.dismissIcon}>
        <button
          onClick={this.handleDismiss}
          data-test-id="alert_close_btn"
          aria-label="Close alert"
        >
          <MLIcon type="x" title="Close" />
        </button>
      </div>
    );
  }

  renderAlertInPlace() {
    const { className, alertType, dismissible, text, linkEl } = this.props;

    const alertProps = getAlertProps(alertType);

    return (
      <div
        {...this.getAlertA11yProps()}
        className={`${className} ${s.alertInPlace} ${alertProps.alertClass} ${this.state.showClass}`}
        data-test-id="alert"
        role="alert"
        aria-live="assertive"
      >
        <div className={s.alertInPlaceIcon}>
          <MLIcon type={alertProps.alertIconType} />
        </div>
        <span className={s.alertText}>
          {text}. {linkEl || null}
        </span>
        {dismissible && this.renderDismissEl()}
      </div>
    );
  }

  renderAlertWithNavContainer() {
    const { className, alertType, dismissible, text, linkEl } = this.props;
    const alertProps = getAlertProps(alertType);

    const navContainer = document.querySelector(navSelector);

    let width;
    let alertLeft;
    if (navContainer) {
      const navContainerWidth = navContainer.clientWidth;
      // eslint-disable-next-line radix
      alertLeft = parseInt(navContainerWidth + 10);
      width = `calc(100% - ${alertLeft + 10}px)`;
    }

    return (
      <div
        {...this.getAlertA11yProps()}
        style={{
          top: this.props.top || 28,
          left: alertLeft,
          width,
        }}
        className={`${className} ${s.alert} ${alertProps.alertClass} ${this.state.showClass}`}
        data-test-id="alert"
        role="alert"
        aria-live="assertive"
      >
        <div className={s.alertIcon}>
          <MLIcon type={alertProps.alertIconType} />
        </div>
        <span className={s.alertText}>
          {text}. {linkEl || null}
        </span>
        {dismissible && this.renderDismissEl()}
      </div>
    );
  }

  render() {
    const { ignoreNavContainer } = this.props;
    return (
      <div>
        {ignoreNavContainer
          ? this.renderAlertInPlace()
          : this.renderAlertWithNavContainer()}
      </div>
    );
  }
}

// eslint-disable-next-line new-cap
export default ReactTimeout(MLAlert);
